import React, { useEffect } from "react"

type SimpleModalProps = {
   isOpen: boolean
   onClose: () => void
   title?: string
   description?: string
   children?: React.ReactNode
   extra?: string
   classname?: string
   skippable?: boolean
}

const SimpleModal = ({
   isOpen,
   onClose,
   title,
   description,
   children,
   extra,
   classname,
   skippable = true,
}: SimpleModalProps) => {
   // Close modal on escape key press
   useEffect(() => {
      const handleEscape = (event: KeyboardEvent) => {
         if (event.key === "Escape") {
            onClose()
         }
      }

      if (isOpen) {
         document.addEventListener("keydown", handleEscape)
         // Prevent background scrolling when modal is open
         document.body.style.overflow = "hidden"
      }

      return () => {
         document.removeEventListener("keydown", handleEscape)
         // Safe cleanup even when modal is not open
         try {
            if (document.body) {
               document.body.style.overflow = "unset"
            }
         } catch (error) {
            console.warn("Could not restore body overflow style:", error)
         }
      }
   }, [isOpen, onClose])

   if (!isOpen) return null

   return (
      <div
         className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60"
         onMouseDown={(e) => {
            if (e.target === e.currentTarget) e.currentTarget.dataset.backdropDown = "true"
            else delete e.currentTarget.dataset.backdropDown
         }}
         onClick={(e) => {
            if (e.target === e.currentTarget && e.currentTarget.dataset.backdropDown === "true") onClose()
         }}
      >
         <div
            className={`relative mx-4 max-h-[95vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white p-6 shadow-lg duration-200 animate-in zoom-in-95 ${classname ?? ""}`}
         >
            {skippable && (
               <button
                  className="absolute right-4 top-4 text-2xl leading-none text-gray-400 hover:text-gray-700"
                  onClick={onClose}
                  aria-label="Close modal"
               >
                  ×
               </button>
            )}
            {title && (
               <h2
                  className={`pr-8 font-bold text-gray-700 sm:text-lg md:text-2xl lg:text-4xl ${extra ? "mb-0" : "mb-1 lg:mb-2"}`}
               >
                  {title}
               </h2>
            )}
            {extra && <h3 className="mb-2 text-2xl font-semibold text-orange-500">{extra}</h3>}
            {description && <p className="mb-4 text-sm text-gray-600 md:text-base lg:text-xl">{description}</p>}
            {children}
         </div>
      </div>
   )
}

export default SimpleModal
